import { Header } from '../components/Header';
import { Loading, ErrorMessage } from 'components';
import { useOnboardingCompanies } from 'lane-shared/contexts/SignUpContext/useOnboardingCompanies';
import { Channel } from 'lane-shared/types/ChannelType';
import { Text } from 'components/typography';
import { WhiteLabelType } from 'lane-shared/types/WhiteLabelType';
import React, { useState, useEffect } from 'react';
import { useWorkplaceOnboardingEnabled } from 'lane-shared/hooks/onboarding/useWorkplaceOnboardingEnabled';
import { OnboardingPreselectedCompany } from './OnboardingPreselectedCompany';
import { OnboardingSelectCompany } from './OnboardingSelectCompany';
import { useTranslation } from 'react-i18next';
import { convertCompanyEdgeToChannelType } from 'components/onboarding/components/ChannelSearchCircleListWrapper/ChannelSearchCircleListWrapper';
import { Flex } from 'design-system-web';
import { OnboardingCompleteData } from '..';
import { ActiveChannelTypeEnum } from 'constants-channel';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';

import styles from './index.scss';

type Props = {
  buildingFromContext: Channel | null;
  userName: string;
  whitelabel: WhiteLabelType;
  onboardingState: 'loading' | 'idle' | 'error';
  errorMessage?: string;
  onComplete: (data: OnboardingCompleteData) => void;
  onCantFindCompany: (building: Channel, fullname: string) => void;
  onBack: () => void;
  onClear: () => void;
  invitedChannel?: Channel;
  presumedEmailDomain?: string;
  companySelectionRequiredDuringSignup?: boolean;
};

const TRANSLATION_KEYS = {
  serverError: 'web.onboarding.building.server.error',
};

export const OnboardingCompany = ({
  userName,
  whitelabel,
  onboardingState,
  errorMessage,
  onComplete,
  onCantFindCompany,
  onBack,
  onClear,
  invitedChannel,
  buildingFromContext,
  presumedEmailDomain,
  companySelectionRequiredDuringSignup = false,
}: Props) => {
  const { t } = useTranslation();
  const isWorkplaceOnboardingEnabled = useWorkplaceOnboardingEnabled();
  const companySelectionFFOn = useFlag(
    FeatureFlag.CompanySelectionRequiredDuringSignup,
    false
  );
  const companySelectionRequired = companySelectionRequiredDuringSignup && companySelectionFFOn;
  const [company, setCompany] = useState<Channel | null>(null);
  const [parentCompany, setParentCompany] = useState<Channel | null>(null);

  const [shouldUsePreselectedCompany, setShouldUsePreselectedCompany] =
    useState(true);

  const isInvitedChannelBuilding =
    invitedChannel?.type === ActiveChannelTypeEnum.Property;
  const invitedBuildingId = isInvitedChannelBuilding
    ? invitedChannel?._id
    : null;
  const buildingId = isWorkplaceOnboardingEnabled
    ? buildingFromContext?._id
    : invitedBuildingId;

  const { companies, isInitialLoading, error } = useOnboardingCompanies({
    query: '',
    buildingId,
    presumedEmailDomain,
  });

  useEffect(() => {
    if (error) {
      window.Toast.show(<Text>{t(TRANSLATION_KEYS.serverError)}</Text>);
    }
  }, [error]);

  const preselectedCompanies = companies.filter(c => c.node.matchesEmailDomain);
  const preselectedCompany =
    preselectedCompanies.length === 1
      ? convertCompanyEdgeToChannelType(preselectedCompanies[0])
      : null;

  const shouldShowPreselectedCompany =
    isWorkplaceOnboardingEnabled &&
    shouldUsePreselectedCompany &&
    preselectedCompany &&
    !invitedChannel;

  const isLoading = onboardingState === 'loading' || isInitialLoading;

  const shouldRedirectDirectlyToLogin =
    !errorMessage &&
    !isLoading &&
    isWorkplaceOnboardingEnabled &&
    (invitedChannel ?? buildingFromContext) &&
    companies.length <= 1;

  const handleOtherCompanyClick = () => {
    setShouldUsePreselectedCompany(false);
    onClear();
  };

  if (shouldRedirectDirectlyToLogin) {
    const company = companies?.[0]
      ? convertCompanyEdgeToChannelType(companies[0])
      : null;
    const building = invitedChannel ?? buildingFromContext;

    if (company && building) {
      onComplete({ company, building, userName });
    } else if (building &&
      !companySelectionRequired
    ) {
      onCantFindCompany(building, userName);
    }
  }

  return (
    <Flex
      direction="column"
      align="center"
      justify="spaceBetween"
      className={styles.container}
    >
      <Header userName={userName} />
      {errorMessage && <ErrorMessage error={errorMessage} />}
      {isLoading && <Loading className={styles.loading} />}
      {!isLoading &&
        (shouldShowPreselectedCompany ? (
          <OnboardingPreselectedCompany
            userName={userName}
            building={buildingFromContext}
            isLoading={isLoading}
            preselectedCompany={preselectedCompany}
            onComplete={onComplete}
            handlers={{
              handleOtherCompanyClick,
            }}
          />
        ) : (
          <OnboardingSelectCompany
            company={company}
            parentCompany={parentCompany}
            setCompany={setCompany}
            setParentCompany={setParentCompany}
            userName={userName}
            whitelabel={whitelabel}
            isLoading={isLoading}
            onComplete={onComplete}
            onBack={onBack}
            onClear={onClear}
            onCantFindCompany={onCantFindCompany}
            invitedChannel={invitedChannel}
            buildingFromContext={buildingFromContext}
            presumedEmailDomain={presumedEmailDomain}
            companySelectionRequiredDuringSignup={companySelectionRequiredDuringSignup}
          />
        ))}
    </Flex>
  );
};
